import { useEffect, useState, useCallback, useRef, useContext } from "react";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { db } from "@data/firebase";
import { useAuth } from "@contexts/AuthContext";
import { ProjectContext } from "@contexts/ProjectContext";

/**
 * Syncs a single Firestore document to React state. The doc shape is
 * `{ value: <anything> }` so we can store arrays/objects directly.
 *
 * Path logic:
 *   - global === true: users/{uid}/data/{key}   (user-private settings)
 *   - otherwise:       projects/{activeProjectId}/data/{key}  (v2)
 *
 * For v2 projects the path is owner-agnostic; membership is enforced by
 * Firestore rules via the projects/{pid}/members subcollection. Seeding
 * defaults still requires owner role — shared members shouldn't create
 * docs on the owner's behalf.
 *
 * Falls back to `defaultValue` while loading or if the doc doesn't exist.
 */
export default function useFirestoreDoc(key, defaultValue, options = {}) {
  const { user } = useAuth();
  const projectCtx = useContext(ProjectContext);
  const [value, setValue] = useState(defaultValue);
  const [loaded, setLoaded] = useState(false);
  const writeTimer = useRef(null);

  const isGlobal = options.global === true;
  const activeProjectId = projectCtx?.activeProjectId;
  const activeProject = projectCtx?.activeProject;
  const isProjectOwner = !!activeProject && activeProject.ownerUid === user?.uid;

  const getPathSegments = useCallback(() => {
    if (!user) return null;
    if (isGlobal) {
      return ["users", user.uid, "data", key];
    }
    if (!activeProjectId) return null;
    return ["projects", activeProjectId, "data", key];
  }, [user, isGlobal, activeProjectId, key]);

  useEffect(() => {
    if (!user) {
      setValue(defaultValue);
      setLoaded(false);
      return;
    }
    const segments = getPathSegments();
    if (!segments) return;
    const ref = doc(db, ...segments);
    const isOwner = isGlobal ? true : isProjectOwner;
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setValue(data.value !== undefined ? data.value : defaultValue);
        } else if (isOwner) {
          // Only owners seed default values — shared members shouldn't create
          // docs on the owner's behalf, and may not have write perms yet.
          setDoc(ref, { value: defaultValue }, { merge: true });
          setValue(defaultValue);
        } else {
          setValue(defaultValue);
        }
        setLoaded(true);
      },
      (err) => {
        console.error(`[useFirestoreDoc:${key}]`, err);
        setLoaded(true);
      }
    );
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, key, getPathSegments]);

  const setAndPersist = useCallback(
    (next) => {
      setValue((prev) => {
        const resolved = typeof next === "function" ? next(prev) : next;
        if (user) {
          if (writeTimer.current) clearTimeout(writeTimer.current);
          writeTimer.current = setTimeout(() => {
            const segments = getPathSegments();
            if (!segments) return;
            const ref = doc(db, ...segments);
            setDoc(ref, { value: resolved }, { merge: true }).catch((err) =>
              console.error(`[useFirestoreDoc:${key}] write failed`, err)
            );
          }, 400);
        }
        return resolved;
      });
    },
    [user, key, getPathSegments]
  );

  return [value, setAndPersist, loaded];
}
